/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment List Item
 *
 * @file AssignmentListItem.jsx
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  ClickAwayListener,
  IconButton,
  Typography
} from '@mui/material';
import Icon from '../icons/Icon';
import mixins from '../../assets/styles/mixins.scss';
import ConditionalWrapper from '../common/ConditionalWrapper';
import ThreeDotActionMenu from '../common/ThreeDotActionMenu';
import ProgressBarWidget from '../common/ProgressBarWidget';
import CustomCircularProgress from '../common/CustomCircularProgress ';
import HtmlTooltip, { StatusTemplate } from '../common/HtmlTooltip';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import { getDateBadgeParts } from './assignmentFormUtils';
import { buildCompletionProgressTooltipSegments } from './assignmentProgressUtils';
import AssignmentDueDateModal from './AssignmentDueDateModal';
import { useAssignmentDueDateModal } from './AssignmentDueDateModalProvider';
import { AssignmentListItemStylesRoot, classes } from './styles/assignmentListItemStyles';
import { getDataAttributes, dataAttributesPropType } from '../../common/dataAttributes';

const SECTION_CHIP_TRUNCATE_LENGTH = 24;

// 32px completion ring with a 4.48px stroke. MUI measures thickness against a 44 unit viewBox.
const COMPLETION_CHART_RADIUS = 16;
const COMPLETION_CHART_THICKNESS = 6.16;

export const STATUS_TAG_VARIANTS = {
  WARNING: 'warning',
  ERROR: 'error',
  INFO: 'info'
};

const normalizeSection = (section) => (
  typeof section === 'object' && section !== null
    ? section
    : { id: section, label: String(section) }
);

const CARD_NESTED_INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, [role="checkbox"], [role="menuitem"], [role="switch"]';

/** Enter/Space on the card shell only — not when focus is on a nested control (chip ✕, clear, remove). */
function shouldIgnoreCardKeyboardActivation(event) {
  const { key, target, currentTarget } = event;

  if (key !== 'Enter' && key !== ' ') {
    return true;
  }

  if (target === currentTarget) {
    return false;
  }

  return Boolean(target.closest?.(CARD_NESTED_INTERACTIVE_SELECTOR));
}

const renderConfigurableIcon = ({
  icon,
  iconName,
  defaultIconName,
  className
}) => {
  if (icon) {
    return icon;
  }

  if (iconName || defaultIconName) {
    return <Icon name={iconName || defaultIconName} className={className} />;
  }

  return null;
};

export const defaultConfig = {
  showCheckbox: false,
  showDate: true,
  showAddDatePlaceholder: false,
  showDueDateEditableIcon: false,
  showDueDateExtensionInfo: false,
  showSuccessAlert: false,
  showIssueAlert: false,
  showEditButton: false,
  showSections: true,
  showProgressBar: false,
  showTopics: false,
  showPublishStatus: false,
  showDragZone: false,
  showMenu: true,
  showRemoveButton: false,
  useFixedTitleColumn: false,
  showTitle: true,
  showSectionRemoveButtons: false,
  clickToEdit: false,
  inlineEditOnSelect: false,
  inlineTitleEdit: false,
  showClearDataButton: false,
  scheduleBuilderLayout: false,
  showStatusTag: false,
  showCompletionChart: false,
  showPerformanceReportButton: false,
  selectionMode: false,
  showDragZoneIcon: true,
  suppressScheduleDateEdit: false,
  modalScheduleAssignMode: false,
  assignTargetAssignmentId: ''
};

const AssignmentListItem = ({
  assignment,
  isSelected,
  textInfo,
  config: configProp,
  onCheckboxChange,
  onEdit,
  onRemove,
  onSelect,
  onThreeDotMenuClick,
  onDueDateClick,
  onContentDrop,
  onSectionRemove,
  onDeselect,
  onTitleChange,
  dataAttributes,
  onPerformanceReportClick
}) => {
  const config = { ...defaultConfig, ...configProp };
  const dueDateModal = useAssignmentDueDateModal();
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [draftTitle, setDraftTitle] = React.useState('');

  const {
    title,
    date,
    sections = [],
    contentNodes = [],
    checked = false,
    showSuccessAlert,
    showIssueAlert,
    progressSegments = [],
    progressTooltipSegments = [],
    topics = [],
    publishStatusLabel,
    publishStatusIcon,
    publishStatusIconName,
    menuItems = [],
    dragZoneLabel,
    hasDueDateExtension,
    hasNoQuestionsWarning = false,
    statusTag,
    completion,
    showPerformanceReportButton
  } = assignment;

  const isInlineEditing = config.inlineEditOnSelect && isSelected;
  const isScheduleBuilder = config.scheduleBuilderLayout;
  const hasAssignedContent = sections.length > 0 || (isScheduleBuilder && contentNodes.length > 0);
  const shouldShowDragZone = config.showDragZone && !hasAssignedContent;
  const shouldShowTitle = !shouldShowDragZone && config.showTitle;
  // When titles are suppressed board-wide (auto-name checkbox on), Sections becomes the
  // primary line of text on the card, so it picks up the emphasis the title would have had.
  const shouldEmphasizeSections = isScheduleBuilder && !config.showTitle;
  const isPopulatedInlineEditing = isScheduleBuilder && isInlineEditing && sections.length > 0;
  const shouldShowInlineTitleEdit = isPopulatedInlineEditing && config.inlineTitleEdit;
  const isEmptySlotSelected = isScheduleBuilder && shouldShowDragZone && isInlineEditing;
  const shouldShowSectionRemoveButtons = sections.length > 0 && (
    config.showSectionRemoveButtons
    || (!isScheduleBuilder && isInlineEditing)
  );
  const shouldShowSchedulePopulatedHoverActions = (
    isScheduleBuilder
    && sections.length > 0
    && !isPopulatedInlineEditing
  );
  const shouldShowClearDataButton = (
    config.showClearDataButton
    && sections.length > 0
    && (!isScheduleBuilder || isPopulatedInlineEditing || shouldShowSchedulePopulatedHoverActions)
  );
  const shouldUseMutedTitle = isPopulatedInlineEditing;
  const shouldShowEmptySlotRemove = isScheduleBuilder && shouldShowDragZone;
  const shouldShowDragZoneRemove = !isScheduleBuilder && isInlineEditing && !sections.length;
  const shouldShowScheduleDateEditIcon = (
    isScheduleBuilder
    && shouldShowDragZone
    && !config.suppressScheduleDateEdit
  );
  const shouldShowPopulatedDateEditIcon = (
    shouldShowSchedulePopulatedHoverActions
    && !config.suppressScheduleDateEdit
  );
  const isScheduleDueDateClickable = (
    isScheduleBuilder
    && !config.suppressScheduleDateEdit
    && Boolean(date)
    && sections.length > 0
    && !shouldShowDragZone
  );
  const shouldShowRemoveButton = config.showRemoveButton && sections.length > 0 && !config.showClearDataButton;
  const shouldShowSuccessAlert = showSuccessAlert ?? config.showSuccessAlert;
  const shouldShowIssueAlert = showIssueAlert ?? config.showIssueAlert;
  const shouldShowStatusTag = config.showStatusTag && Boolean(statusTag?.label);
  const shouldShowCompletionChart = config.showCompletionChart && Number(completion?.total) > 0;
  const shouldShowPerformanceReportButton = showPerformanceReportButton
    ?? config.showPerformanceReportButton;
  const hasStatusCell = shouldShowStatusTag || shouldShowCompletionChart || shouldShowPerformanceReportButton;

  React.useEffect(() => {
    if (shouldShowInlineTitleEdit) {
      setDraftTitle(title);
    }
  }, [shouldShowInlineTitleEdit, title, assignment.id]);

  const commitDraftTitle = () => {
    const trimmedTitle = draftTitle.trim();

    if (!trimmedTitle || trimmedTitle === title) {
      setDraftTitle(title);

      return;
    }

    onTitleChange?.(assignment, trimmedTitle);
  };

  const handleTitleInputChange = (event) => {
    setDraftTitle(event.target.value);
  };

  const handleTitleInputClick = (event) => {
    event.stopPropagation();
  };

  const handleTitleInputKeyDown = (event) => {
    event.stopPropagation();

    if (event.key === 'Enter') {
      event.preventDefault();
      commitDraftTitle();
    }
  };

  const handleTitleInputBlur = () => {
    commitDraftTitle();
  };

  const handleRootClick = () => {
    if (config.selectionMode) {
      onSelect?.(assignment);

      return;
    }

    if (config.modalScheduleAssignMode) {
      onSelect?.(assignment);

      return;
    }

    onSelect?.(assignment);
  };

  const handleCardKeyDown = (event) => {
    if (shouldIgnoreCardKeyboardActivation(event)) {
      return;
    }

    event.preventDefault();
    handleRootClick();
  };

  const handleMenuOpenChange = (isOpen) => {
    onThreeDotMenuClick?.(isOpen);
  };

  const handleCheckboxClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onCheckboxChange?.(assignment, !checked);
  };

  const handleEditClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onEdit?.(assignment);
  };

  const handleDeselectClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onDeselect?.(assignment);
  };

  const handleRemoveClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onRemove?.(assignment);
  };

  const handleClearDataClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onRemove?.(assignment);
  };

  const handleDragOver = (event) => {
    if (config.selectionMode || (!config.showDragZone && !onContentDrop)) {
      return;
    }

    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    if (config.selectionMode) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const rawPayload = event.dataTransfer.getData('application/json');

    if (!rawPayload || !onContentDrop) {
      return;
    }

    onContentDrop(assignment, rawPayload);
  };

  const handlePerformanceReportClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onPerformanceReportClick?.(assignment);
  };

  // Keeps Enter/Space on the button from also activating the card.
  const handlePerformanceReportKeyDown = (event) => {
    event.stopPropagation();
  };

  const handleSectionRemoveClick = (event, sectionNumber) => {
    event.preventDefault();
    event.stopPropagation();
    onSectionRemove?.(assignment, sectionNumber);
  };

  // Clicking anywhere outside the card being inline-edited closes it, same as clicking it
  // again — no need to click another slot just to back out of the current one.
  const handleClickAway = () => {
    if (shouldShowInlineTitleEdit) {
      commitDraftTitle();
    }

    onDeselect?.(assignment);
  };

  const handleDueDateClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (dueDateModal) {
      dueDateModal.openDueDateModal(assignment);

      return;
    }

    onDueDateClick?.(assignment);
  };

  const renderCheckbox = () => {
    if (!config.showCheckbox) {
      return null;
    }

    return (
      <IconButton
        className={classes.checkboxButton}
        aria-label={checked ? 'Deselect assignment' : 'Select assignment'}
        onClick={handleCheckboxClick}
      >
        <Icon name={checked ? 'CheckboxCheckedIcon' : 'CheckboxOutlineIcon'} />
      </IconButton>
    );
  };

  const renderDateBadge = () => {
    if (!config.showDate) {
      return null;
    }

    const hasDate = Boolean(date);
    const showAddDate = !hasDate && config.showAddDatePlaceholder;

    if (showAddDate) {
      const addDateInteractive = !config.suppressScheduleDateEdit;
      const addDateContent = (
        <>
          {addDateInteractive && isScheduleBuilder && (
            <div className={[
              classes.dueDateEditableIconContainer,
              classes.scheduleDateEditIcon
            ].join(' ')}
            >
              <Icon name="CaretUpIcon" className={classes.dueDateEditableIcon} />
            </div>
          )}
          <Icon name="AddDateCalendarIcon" className={classes.addDateIcon} />
          <span className={classes.addDateLabel}>{textInfo.addDateLabel}</span>
        </>
      );

      if (!addDateInteractive) {
        return (
          <div className={`${classes.addDateBadge} ${classes.addDateBadgeStatic}`}>
            {addDateContent}
          </div>
        );
      }

      return (
        <button
          type="button"
          className={`${classes.addDateBadge} ${classes.dateBadge} ${classes.dateBadgeButton}`}
          onClick={handleDueDateClick}
          aria-label={textInfo.addDateLabel}
        >
          {addDateContent}
        </button>
      );
    }

    const dateBadge = getDateBadgeParts(date);
    const shouldShowDateBadgeEditIcon = (
      (hasDate && config.showDueDateEditableIcon)
      || shouldShowPopulatedDateEditIcon
      || shouldShowScheduleDateEditIcon
    );
    const isDueDateEditable = hasDate && (shouldShowDateBadgeEditIcon || isScheduleDueDateClickable);
    const shouldHideDateCaretUntilHover = (
      shouldShowScheduleDateEditIcon || shouldShowPopulatedDateEditIcon
    );

    const renderDueDateExtensionInfo = () => {
      const shouldShow = config.showDueDateExtensionInfo && Boolean(hasDueDateExtension);

      if (!shouldShow || !hasDate) {
        return null;
      }

      const handleInfoClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
      };

      return (
        <TooltipWrapper
          tooltip={textInfo.dueDateExtensionInfoTooltip}
          placement="top"
        >
          <button
            type="button"
            className={classes.dueDateExtensionInfoContainer}
            onClick={handleInfoClick}
            aria-label={textInfo.dueDateExtensionInfoTooltip}
          >
            <Icon name="InfoIcon" className={classes.dueDateExtensionInfoIcon} />
          </button>
        </TooltipWrapper>
      );
    };

    const dateBadgeContent = (
      <>
        {renderDueDateExtensionInfo()}
        {shouldShowDateBadgeEditIcon && (
          <div className={[
            classes.dueDateEditableIconContainer,
            shouldHideDateCaretUntilHover && classes.scheduleDateEditIcon
          ].filter(Boolean).join(' ')}
          >
            <Icon name="CaretUpIcon" className={classes.dueDateEditableIcon} />
          </div>
        )}
        <span className={classes.dateBadgeWeekday}>{dateBadge.weekday}</span>
        <span className={classes.dateBadgeMonth}>{dateBadge.month}</span>
        <span className={classes.dateBadgeDay}>{dateBadge.day}</span>
      </>
    );

    if (isDueDateEditable) {
      return (
        <button
          type="button"
          className={`${classes.dateBadge} ${classes.dateBadgeButton}`}
          onClick={handleDueDateClick}
          aria-label={textInfo.dueDateModalTitle || 'Edit due date'}
        >
          {dateBadgeContent}
        </button>
      );
    }

    return (
      <div className={classes.dateBadge}>
        {dateBadgeContent}
      </div>
    );
  };

  const renderStatusIcons = () => (
    <>
      {shouldShowSuccessAlert && (
        <Icon name="InlineSuccessIcon" className={classes.statusIcon} />
      )}
      {shouldShowIssueAlert && (
        <Icon name="InlineWarningIcon" className={classes.statusIcon} />
      )}
    </>
  );

  const renderEditButton = () => {
    if (!config.showEditButton) {
      return null;
    }

    return (
      <IconButton
        className={classes.editButton}
        aria-label="Edit assignment"
        onClick={handleEditClick}
      >
        <Icon name="EditActionIcon" />
      </IconButton>
    );
  };

  const renderClearDataButton = () => {
    if (!shouldShowClearDataButton) {
      return null;
    }

    const shouldRevealClearOnHover = (
      (!isScheduleBuilder && sections.length > 0)
      || shouldShowSchedulePopulatedHoverActions
    );

    return (
      <TooltipWrapper
        tooltip={textInfo.clearDataLabel || 'Clear data'}
        placement="top"
      >
        <IconButton
          className={[
            classes.clearDataButton,
            shouldRevealClearOnHover && classes.clearDataButtonHoverReveal
          ].filter(Boolean).join(' ')}
          aria-label={textInfo.clearDataLabel || 'Clear data'}
          onClick={handleClearDataClick}
        >
          <Icon name="DeleteActionIcon" />
        </IconButton>
      </TooltipWrapper>
    );
  };

  const renderTitle = () => {
    if (!shouldShowTitle) {
      return null;
    }

    if (shouldShowInlineTitleEdit) {
      return (
        <div className={classes.scheduleTitleEditRow}>
          <div className={classes.scheduleTitleGroup}>
            <label
              htmlFor={`assignment-title-${assignment.id}`}
              className={classes.scheduleTitleLabel}
            >
              {textInfo.assignmentNameLabel || 'Assignment name'}
            </label>
            <div className={classes.scheduleTitleInputRow}>
              <input
                id={`assignment-title-${assignment.id}`}
                type="text"
                className={classes.scheduleTitleInput}
                value={draftTitle}
                placeholder={textInfo.assignmentNameValue || textInfo.assignmentNamePlaceholder}
                onChange={handleTitleInputChange}
                onClick={handleTitleInputClick}
                onKeyDown={handleTitleInputKeyDown}
                onBlur={handleTitleInputBlur}
              />
              {shouldShowClearDataButton && (
                <div className={classes.scheduleTitleInlineDelete}>
                  {renderClearDataButton()}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    const titleClassName = [
      classes.itemTitle,
      shouldUseMutedTitle && classes.itemTitleMuted
    ].filter(Boolean).join(' ');

    return (
      <div className={classes.titleRow}>
        {renderStatusIcons()}
        <div className={classes.titleGroup}>
          <TooltipWrapper tooltip={title} placement="top">
            <Typography className={titleClassName} component="h3">
              {title}
            </Typography>
          </TooltipWrapper>
          {renderEditButton()}
        </div>
      </div>
    );
  };

  const renderSectionChipLabel = (label) => {
    const isTruncated = label.length > SECTION_CHIP_TRUNCATE_LENGTH;
    const displayLabel = isTruncated
      ? `${label.slice(0, SECTION_CHIP_TRUNCATE_LENGTH)}…`
      : label;

    const labelElement = (
      <span className={classes.sectionChipLabel}>{displayLabel}</span>
    );

    if (!isTruncated) {
      return labelElement;
    }

    return (
      <TooltipWrapper tooltip={label} placement="top">
        {labelElement}
      </TooltipWrapper>
    );
  };

  const renderSections = () => {
    if (!config.showSections || !sections || !sections.length) {
      return null;
    }

    const normalizedSections = sections.map(normalizeSection);

    const sectionChipClassName = [
      classes.sectionChip,
      config.showAddDatePlaceholder && !isScheduleBuilder && classes.sectionChipMuted,
      shouldEmphasizeSections && classes.sectionChipEmphasized
    ].filter(Boolean).join(' ');

    const sectionsPrefixClassName = [
      classes.sectionsPrefix,
      shouldEmphasizeSections && classes.sectionsPrefixEmphasized
    ].filter(Boolean).join(' ');

    const sectionChips = normalizedSections.map((section) => (
      <span key={section.id} className={sectionChipClassName}>
        {shouldShowSectionRemoveButtons && (
          <button
            type="button"
            className={classes.sectionChipRemoveButton}
            onClick={(event) => handleSectionRemoveClick(event, section.id)}
            aria-label={`Remove section ${section.label}`}
          >
            <Icon name="CloseIcon" />
          </button>
        )}
        {renderSectionChipLabel(section.label)}
      </span>
    ));

    const sectionsRowClassName = [
      classes.sectionsRow,
      shouldEmphasizeSections && classes.sectionsRowStacked
    ].filter(Boolean).join(' ');

    const sectionsChipsScrollClassName = [
      classes.sectionsChipsScroll,
      shouldEmphasizeSections && classes.sectionsChipsScrollStacked
    ].filter(Boolean).join(' ');

    return (
      <div className={sectionsRowClassName}>
        <span className={sectionsPrefixClassName}>{textInfo.sectionsPrefix}</span>
        <div className={sectionsChipsScrollClassName}>
          {sectionChips}
        </div>
      </div>
    );
  };

  const renderNoQuestionsWarning = () => {
    if (!isScheduleBuilder || !hasNoQuestionsWarning) {
      return null;
    }

    return (
      <div className={classes.noQuestionsWarningRow} role="alert">
        <Icon name="CautionIcon" className={classes.noQuestionsWarningIcon} />
        <span>{textInfo.noQuestionsWarningLabel}</span>
      </div>
    );
  };

  const renderItemContent = () => {
    if (shouldShowDragZone) {
      return null;
    }

    const content = (
      <>
        {renderTitle()}
        {renderSections()}
        {renderNoQuestionsWarning()}
      </>
    );

    return (
      <div className={classes.itemContent}>
        {content}
      </div>
    );
  };

  const renderProgressBar = () => {
    if (!config.showProgressBar || !progressSegments.length) {
      return null;
    }

    const tooltipSegments = progressTooltipSegments.length
      ? buildCompletionProgressTooltipSegments(progressTooltipSegments)
      : buildCompletionProgressTooltipSegments(progressSegments);

    return (
      <div className={classes.middleCell}>
        <HtmlTooltip
          arrow
          placement="bottom"
          title={<StatusTemplate segments={tooltipSegments} isPercentage={false} showBullet />}
        >
          <div className={classes.progressBarWrapper}>
            <ProgressBarWidget segments={progressSegments} height={6} />
          </div>
        </HtmlTooltip>
      </div>
    );
  };

  const renderTopics = () => {
    if (!config.showTopics || !topics.length) {
      return null;
    }

    return (
      <div className={classes.topicsCell}>
        {topics.map((topic) => (
          <span key={topic} className={classes.topicChip}>
            {topic}
          </span>
        ))}
      </div>
    );
  };

  const renderPublishStatus = () => {
    if (!config.showPublishStatus) {
      return null;
    }

    return (
      <div className={classes.publishStatusCell}>
        {renderConfigurableIcon({
          icon: publishStatusIcon,
          iconName: publishStatusIconName,
          defaultIconName: 'EyeSlashIcon',
          className: classes.publishStatusIcon
        })}
        <Typography className={classes.publishStatusLabel} component="p">
          {publishStatusLabel || textInfo.notPublishedLabel}
        </Typography>
      </div>
    );
  };

  const renderDragZone = () => {
    if (!shouldShowDragZone) {
      return null;
    }

    const emptyLabel = textInfo.addToAssignmentModalEmptyLabel || 'No content yet';
    const zoneLabel = config.showDragZoneIcon === false
      ? emptyLabel
      : (dragZoneLabel || textInfo.dragZoneLabel);

    const dragZoneClassName = [
      classes.dragZone,
      isDragOver && classes.dragZoneActive,
      !isScheduleBuilder && isInlineEditing && classes.dragZoneInlineEditing,
      config.showDragZoneIcon === false && classes.dragZoneAddToAssignmentEmpty
    ].filter(Boolean).join(' ');

    return (
      <div
        className={dragZoneClassName}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {shouldShowDragZoneRemove && (
          <TooltipWrapper
            tooltip={textInfo.removeLabel || 'Remove'}
            placement="top"
          >
            <button
              type="button"
              className={classes.dragZoneRemoveButton}
              onClick={handleDeselectClick}
              aria-label={textInfo.removeLabel || 'Remove'}
            >
              <Icon name="CloseIcon" />
            </button>
          </TooltipWrapper>
        )}
        {config.showDragZoneIcon !== false && (
          <Icon name="UploadIcon" className={classes.dragZoneIcon} />
        )}
        <Typography
          className={[
            classes.dragZoneLabel,
            config.showDragZoneIcon === false && classes.dragZoneEmptyPlaceholderLabel
          ].filter(Boolean).join(' ')}
          component="p"
        >
          {zoneLabel}
        </Typography>
      </div>
    );
  };

  const renderEmptySlotRemoveButton = () => {
    if (!shouldShowEmptySlotRemove) {
      return null;
    }

    return (
      <TooltipWrapper
        tooltip={textInfo.removeLabel || 'Remove'}
        placement="top"
      >
        <IconButton
          className={classes.emptySlotRemoveButton}
          aria-label={textInfo.removeLabel || 'Remove'}
          onClick={handleRemoveClick}
        >
          <Icon name="CloseIcon" />
        </IconButton>
      </TooltipWrapper>
    );
  };

  const renderStatusTag = () => {
    if (!shouldShowStatusTag) {
      return null;
    }

    const variantClassName = {
      [STATUS_TAG_VARIANTS.WARNING]: classes.statusTagWarning,
      [STATUS_TAG_VARIANTS.ERROR]: classes.statusTagError,
      [STATUS_TAG_VARIANTS.INFO]: classes.statusTagInfo
    }[statusTag.variant];

    return (
      <span className={[classes.statusTag, variantClassName].filter(Boolean).join(' ')}>
        {statusTag.label}
      </span>
    );
  };

  const renderCompletionChart = () => {
    if (!shouldShowCompletionChart) {
      return null;
    }

    const { completed = 0, total } = completion;

    return (
      <span className={classes.completionChart}>
        <CustomCircularProgress
          radius={COMPLETION_CHART_RADIUS}
          borderWidth={COMPLETION_CHART_THICKNESS}
          value={(completed / total) * 100}
          color={mixins.amethystColor}
          incompleteColor={mixins.amethystLightColor}
          aria-hidden="true"
        />
        <span>
          <span className={classes.completionChartCount}>{`${completed}/${total}`}</span>
          {` ${textInfo.completionLabel}`}
        </span>
      </span>
    );
  };

  const renderPerformanceReportButton = () => {
    if (!shouldShowPerformanceReportButton) {
      return null;
    }

    return (
      <button
        type="button"
        className={classes.performanceReportButton}
        onClick={handlePerformanceReportClick}
        onKeyDown={handlePerformanceReportKeyDown}
      >
        {textInfo.performanceReportLabel}
      </button>
    );
  };

  const renderStatusCell = () => {
    if (!hasStatusCell) {
      return null;
    }

    return (
      <div className={classes.statusCell}>
        {renderStatusTag()}
        {renderCompletionChart()}
        {renderPerformanceReportButton()}
      </div>
    );
  };

  const renderMiddleCells = () => {
    if (shouldShowDragZone) {
      return renderDragZone();
    }

    if (config.showPublishStatus) {
      return renderPublishStatus();
    }

    return (
      <>
        {renderProgressBar()}
        {renderTopics()}
        {renderStatusCell()}
      </>
    );
  };

  const renderMenuIcon = () => {
    if (!config.showMenu || !menuItems.length) {
      return null;
    }

    return (
      <ThreeDotActionMenu
        menuItems={menuItems}
        ariaLabel="Assignment options"
        onOpenChange={handleMenuOpenChange}
      />
    );
  };

  const renderRemoveButton = () => {
    if (!shouldShowRemoveButton) {
      return null;
    }

    return (
      <IconButton
        className={classes.removeButton}
        aria-label="Remove assignment"
        onClick={handleRemoveClick}
      >
        <Icon name="CloseIcon" />
      </IconButton>
    );
  };

  const renderActions = () => {
    const hasClearDataAction = shouldShowClearDataButton
      || (config.showClearDataButton && sections.length > 0 && !isScheduleBuilder);

    if (!config.showMenu && !shouldShowRemoveButton && !hasClearDataAction && !shouldShowEmptySlotRemove) {
      return null;
    }

    return (
      <div className={classes.actionsCell}>
        {hasClearDataAction && renderClearDataButton()}
        {renderEmptySlotRemoveButton()}
        {renderMenuIcon()}
        {renderRemoveButton()}
      </div>
    );
  };

  const itemClassName = [
    classes.item,
    isScheduleBuilder && classes.itemScheduleBuilder,
    isScheduleBuilder && shouldShowDragZone && classes.itemScheduleEmpty,
    isScheduleBuilder && sections.length > 0 && !isPopulatedInlineEditing && classes.itemSchedulePopulated,
    shouldShowSchedulePopulatedHoverActions && classes.itemSchedulePopulatedHoverActions,
    isPopulatedInlineEditing && classes.itemPopulatedEditing,
    isEmptySlotSelected && classes.itemEmptySelected,
    config.selectionMode && isSelected && classes.itemScheduleSelectionSelected,
    !isScheduleBuilder && isSelected && !isInlineEditing && classes.itemSelected,
    !isScheduleBuilder && isInlineEditing && classes.itemInlineEditing,
    !isScheduleBuilder && sections.length > 0 && classes.itemHasSections,
    !isScheduleBuilder && config.clickToEdit && sections.length > 0 && classes.itemClickToEdit,
    !isScheduleBuilder && config.showClearDataButton && sections.length > 0 && classes.itemClearDataOnHover,
    hasStatusCell && classes.itemHasStatusCell
  ].filter(Boolean).join(' ');

  const middleContentClassName = [
    classes.middleContent,
    config.useFixedTitleColumn && classes.middleContentFixedTitle,
    config.scheduleBuilderLayout && classes.middleContentScheduleBuilder
  ].filter(Boolean).join(' ');

  const modalTextInfo = dueDateModal?.textInfo || textInfo;

  const cardAccessibleLabel = (() => {
    if (title && config.showTitle) {
      return title;
    }

    if (date) {
      const parts = getDateBadgeParts(date);

      return [parts.weekday, parts.month, parts.day].filter(Boolean).join(' ') || date;
    }

    return textInfo.addDateLabel;
  })();

  const cardElement = (
    <AssignmentListItemStylesRoot
      className={itemClassName}
      onClick={handleRootClick}
      onDragOver={!config.selectionMode && onContentDrop ? handleDragOver : undefined}
      onDragLeave={!config.selectionMode && onContentDrop ? handleDragLeave : undefined}
      onDrop={!config.selectionMode && onContentDrop ? handleDrop : undefined}
      role="group"
      // Scroll hook for Quick Schedule step 2; hosts own `data-assignment-id` on their row wrappers.
      data-schedule-slot-id={isScheduleBuilder ? assignment.id : undefined}
      aria-label={cardAccessibleLabel}
      tabIndex={0}
      {...getDataAttributes(dataAttributes)}
      onKeyDown={handleCardKeyDown}
    >
      {renderCheckbox()}
      <div className={classes.main}>
        {renderDateBadge()}
        <div className={middleContentClassName}>
          {renderItemContent()}
          {renderMiddleCells()}
        </div>
        {renderActions()}
      </div>
    </AssignmentListItemStylesRoot>
  );

  return (
    <>
      <ConditionalWrapper
        condition={isInlineEditing}
        wrapper={ClickAwayListener}
        wrapperProps={{
          mouseEvent: 'onMouseDown',
          touchEvent: 'onTouchStart',
          onClickAway: handleClickAway
        }}
      >
        {cardElement}
      </ConditionalWrapper>
      {dueDateModal?.activeAssignmentId === assignment.id && (
        <AssignmentDueDateModal
          open
          assignment={assignment}
          textInfo={modalTextInfo}
          onClose={dueDateModal.closeDueDateModal}
          onDone={dueDateModal.handleDone}
        />
      )}
    </>
  );
};

AssignmentListItem.propTypes = {
  assignment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    date: PropTypes.string,
    sections: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired
        })
      ])
    ),
    contentNodes: PropTypes.arrayOf(PropTypes.object),
    checked: PropTypes.bool,
    showSuccessAlert: PropTypes.bool,
    showIssueAlert: PropTypes.bool,
    progressSegments: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
      color: PropTypes.string
    })),
    progressTooltipSegments: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      color: PropTypes.string
    })),
    topics: PropTypes.arrayOf(PropTypes.string),
    publishStatusLabel: PropTypes.string,
    publishStatusIconName: PropTypes.string,
    publishStatusIcon: PropTypes.node,
    dragZoneLabel: PropTypes.string,
    hasDueDateExtension: PropTypes.bool,
    hasNoQuestionsWarning: PropTypes.bool,
    menuItems: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      divider: PropTypes.bool,
      dataAttributes: dataAttributesPropType
    })),
    statusTag: PropTypes.shape({
      label: PropTypes.string.isRequired,
      variant: PropTypes.oneOf(Object.values(STATUS_TAG_VARIANTS))
    }),
    completion: PropTypes.shape({
      completed: PropTypes.number,
      total: PropTypes.number
    }),
    showPerformanceReportButton: PropTypes.bool
  }).isRequired,
  isSelected: PropTypes.bool,
  textInfo: PropTypes.object.isRequired,
  config: PropTypes.object,
  onCheckboxChange: PropTypes.func,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func,
  onSelect: PropTypes.func,
  onThreeDotMenuClick: PropTypes.func,
  onDueDateClick: PropTypes.func,
  onContentDrop: PropTypes.func,
  onSectionRemove: PropTypes.func,
  onDeselect: PropTypes.func,
  onTitleChange: PropTypes.func,
  dataAttributes: dataAttributesPropType,
  onPerformanceReportClick: PropTypes.func
};

AssignmentListItem.defaultProps = {
  isSelected: false,
  config: defaultConfig,
  onCheckboxChange: null,
  onEdit: null,
  onRemove: null,
  onSelect: null,
  onThreeDotMenuClick: null,
  onDueDateClick: null,
  onContentDrop: null,
  onSectionRemove: null,
  onDeselect: null,
  onTitleChange: null,
  dataAttributes: {},
  onPerformanceReportClick: null
};

export default AssignmentListItem;
